import { eventTracker } from 'app/modules/Segment';
import {
	FeatureEnum,
	GroupIdEnum,
	OwnerEnum,
	TContext,
	TProperties,
} from 'app/modules/Segment/types';

export const MGM_EVENTS = {
	MGM_SIDEBAR_BANNER_LINK_COPIED: 'mgm_sidebar_banner_link_copied',
	MGM_INVITE_MODAL_LINK_COPIED: 'mgm_invite_modal_link_copied',
	MGM_INVITE_MODAL_WHATSAPP_SHARED: 'mgm_invite_modal_whatsapp_shared',
	MGM_INVITE_MODAL_EMAIL_SHARED: 'mgm_invite_modal_email_shared',
	MGM_INVITE_MODAL_INFO_CLICKED: 'mgm_invite_modal_info_clicked',
	MGM_INDICATION_PROGRESS_INDICATE_CLICKED:
		'mgm_indication_progress_indicate_clicked',
	MGM_INDICATION_PROGRESS_SAIBA_MAIS_CLICKED:
		'mgm_indication_progress_saiba_mais_clicked',
	MGM_DASH_TASK_CARD_CLICKED: 'mgm_dash_task_card_clicked',
} as const;

export type MgmIndicationStatus = 'initial' | 'in_progress';

const MGM_LIBRARY_NAME = 'gamification-library';
const MGM_FEATURE = FeatureEnum.NOT_FEATURE;

function buildBaseProperties(
	eventText: string,
	eventMeaning: string,
	overrides: Partial<TProperties> = {},
): TProperties {
	return {
		owner: OwnerEnum.INOVACAO,
		feature: MGM_FEATURE,
		event_meaning: eventMeaning,
		relevant_user_activity: true,
		event_text: eventText,
		...overrides,
	} as TProperties;
}

function buildContext(componentName: string): TContext {
	return {
		groupId: GroupIdEnum.GENERAL_GROUP,
		library: {
			name: MGM_LIBRARY_NAME,
			componente: componentName,
		},
	};
}

export function trackMgmSidebarBannerLinkCopied(props: {
	clientId?: string;
} = {}): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_SIDEBAR_BANNER_LINK_COPIED,
		properties: buildBaseProperties(
			'MGM Sidebar Banner Link Copied',
			'Disparado ao copiar o link de indicação no banner do dropdown da sidebar. Propriedades: `client_id`.',
			{
				client_id: props.clientId ?? null,
				component_location: 'sidebar_dropdown_menu',
			},
		),
		context: buildContext('button-component'),
	});
}

export function trackMgmInviteModalLinkCopied(props: {
	clientId?: string;
} = {}): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_INVITE_MODAL_LINK_COPIED,
		properties: buildBaseProperties(
			'MGM Invite Modal Link Copied',
			'Disparado ao copiar o link de indicação dentro do modal de convite. Propriedades: `client_id`.',
			{
				client_id: props.clientId ?? null,
				component_location: 'invite_modal',
			},
		),
		context: buildContext('button-component'),
	});
}

export function trackMgmInviteModalWhatsAppShared(props: {
	clientId?: string;
} = {}): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_INVITE_MODAL_WHATSAPP_SHARED,
		properties: buildBaseProperties(
			'MGM Invite Modal WhatsApp Shared',
			'Disparado ao clicar no botão de compartilhar via WhatsApp no modal de convite. Propriedades: `client_id`.',
			{
				client_id: props.clientId ?? null,
				component_location: 'invite_modal',
			},
		),
		context: buildContext('button-component'),
	});
}

export function trackMgmInviteModalEmailShared(props: {
	clientId?: string;
} = {}): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_INVITE_MODAL_EMAIL_SHARED,
		properties: buildBaseProperties(
			'MGM Invite Modal Email Shared',
			'Disparado ao clicar no botão de compartilhar por email no modal de convite. Propriedades: `client_id`.',
			{
				client_id: props.clientId ?? null,
				component_location: 'invite_modal',
			},
		),
		context: buildContext('button-component'),
	});
}

export function trackMgmInviteModalInfoClicked(): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_INVITE_MODAL_INFO_CLICKED,
		properties: buildBaseProperties(
			'MGM Invite Modal Info Clicked',
			'Disparado ao clicar em "Mais informações" no modal de convite.',
			{ component_location: 'invite_modal' },
		),
		context: buildContext('link-component'),
	});
}

export function trackMgmIndicateClicked(props: {
	location: string;
	count?: number;
}): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_INDICATION_PROGRESS_INDICATE_CLICKED,
		properties: buildBaseProperties(
			'MGM Indication Progress Indicate Clicked',
			'Disparado ao clicar no botão "Indicar um colega" no widget de progresso de indicação. Propriedades: `component_location`, `referral_count`.',
			{
				component_location: props.location,
				referral_count: props.count ?? null,
			},
		),
		context: buildContext('button-component'),
	});
}

export function trackMgmSaibaMaisClicked(props: { location: string }): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_INDICATION_PROGRESS_SAIBA_MAIS_CLICKED,
		properties: buildBaseProperties(
			'MGM Indication Progress Saiba Mais Clicked',
			'Disparado ao clicar em "Saiba mais" no widget de progresso de indicação. Propriedades: `component_location`.',
			{ component_location: props.location },
		),
		context: buildContext('link-component'),
	});
}


export function trackMgmDashTaskCardClicked(props: {
	count: number;
	credits: number;
	status: MgmIndicationStatus;
	location?: string;
}): void {
	eventTracker.track({
		event: MGM_EVENTS.MGM_DASH_TASK_CARD_CLICKED,
		properties: buildBaseProperties(
			'MGM Dash Task Card Clicked',
			'Disparado ao clicar no card MGM fixado na seção "Suas próximas tarefas" do Dashboard. Propriedades: `component_location`, `referral_count`, `referral_credits`, `indication_status`.',
			{
				component_location: props.location ?? 'dashboard_next_tasks',
				referral_count: props.count,
				referral_credits: props.credits,
				indication_status: props.status,
			},
		),
		context: buildContext('card-component'),
	});
}
